import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { TooltipProps } from "recharts";

import { CHART_AXIS_TICK, pieColor } from "../../constants/chartTheme";
import type { CostBreakdownRow } from "../../types";
import { formatINR } from "../../utils/format";
import "./CostBreakdownChart.css";

type Props = {
  rows: CostBreakdownRow[];
  /** "pie" for the Overview Event Cost chart, "bars" for P&L Expense Analysis. */
  variant: "pie" | "bars";
  height?: number;
};

type CostTooltipProps = TooltipProps<number, string> & { total: number };

function CostTooltip({ active, payload, total }: CostTooltipProps) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  const row = item.payload as CostBreakdownRow | undefined;
  const value = Number(item.value) || 0;
  const category = row?.category ?? String(item.name ?? "Cost");
  const share = total > 0 ? `${Math.round((value / total) * 100)}%` : null;
  return (
    <div className="cost-breakdown-chart__tooltip">
      <div className="cost-breakdown-chart__tooltip-name">{category}</div>
      <div className="cost-breakdown-chart__tooltip-value">
        {formatINR(value)}
        {share ? <span className="cost-breakdown-chart__tooltip-share"> · {share}</span> : null}
      </div>
    </div>
  );
}

/**
 * Cost split by category, from the dashboard's ``chart_data.cost_breakdown``.
 *
 * Deliberately one component with two marks: the Overview pie and the P&L
 * Expense Analysis bars must never disagree, so they share a data path and
 * only the rendering differs.
 */
export const CostBreakdownChart: React.FC<Props> = ({ rows, variant, height = 240 }) => {
  const data = useMemo(() => rows.filter((row) => row.amount > 0), [rows]);
  const total = useMemo(() => data.reduce((sum, row) => sum + row.amount, 0), [data]);

  if (data.length === 0) {
    return (
      <p className="eventos-subtle">
        No costs recorded yet. Add artist fee, marketing or production costs in Budget &amp; Costs.
      </p>
    );
  }

  const tooltip = (
    <Tooltip
      content={<CostTooltip total={total} />}
      wrapperStyle={{ outline: "none", zIndex: 20 }}
      allowEscapeViewBox={{ x: true, y: true }}
      cursor={variant === "bars" ? { fill: "rgba(255,255,255,0.05)" } : undefined}
    />
  );

  if (variant === "pie") {
    return (
      <div className="cost-breakdown-chart">
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={data}
              dataKey="amount"
              nameKey="category"
              cx="50%"
              cy="50%"
              outerRadius={90}
              stroke="none"
              isAnimationActive={false}
            >
              {data.map((row, index) => (
                <Cell key={row.category} fill={pieColor(index)} />
              ))}
            </Pie>
            {tooltip}
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="cost-breakdown-chart">
      <ResponsiveContainer width="100%" height={Math.max(height, data.length * 38)}>
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
          <XAxis type="number" tick={CHART_AXIS_TICK} tickFormatter={(v) => formatINR(Number(v))} />
          <YAxis
            type="category"
            dataKey="category"
            tick={CHART_AXIS_TICK}
            width={110}
            interval={0}
          />
          {tooltip}
          <Bar dataKey="amount" radius={[0, 6, 6, 0]} isAnimationActive={false}>
            {data.map((row, index) => (
              <Cell key={row.category} fill={pieColor(index)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CostBreakdownChart;
